/**
 * Lobby mesh diagnostics — always loud in the browser console.
 *
 * Chrome often hides console.info ("Verbose"/Info off). This module uses
 * console.warn + console.log so Default levels always show the lines.
 *
 * Disable: localStorage.dechessLobbyDebug = '0'
 * Force on:  localStorage.removeItem('dechessLobbyDebug')
 * Copy last: copyDeChessLobbyDiag()
 */

import { APP_VERSION } from '../app/release.js';

const MAX_EVENTS = 100;
const recentEvents = [];
let lastReport = null;
let bridgeInstalled = false;
let bootPrinted = false;

function isTestRuntime() {
  return Boolean(import.meta.env?.MODE === 'test' || import.meta.env?.VITEST);
}

export function isLobbyDebugEnabled() {
  if (isTestRuntime() || import.meta.env?.VITE_LOBBY_DEBUG === '0') {
    return false;
  }
  if (typeof localStorage === 'undefined') {
    return true;
  }
  try {
    // Explicit off only. Missing key / '1' / 'true' => on.
    return localStorage.getItem('dechessLobbyDebug') !== '0';
  } catch {
    return true;
  }
}

function pushEvent(event, details) {
  recentEvents.push({
    t: new Date().toISOString(),
    event,
    ...details
  });
  while (recentEvents.length > MAX_EVENTS) {
    recentEvents.shift();
  }
}

export function getLobbyDebugEvents() {
  return [...recentEvents];
}

export function clearLobbyDebugEvents() {
  recentEvents.length = 0;
  lastReport = null;
}

function emitLine(level, message, payload) {
  if (typeof console === 'undefined') {
    return;
  }
  const fn = console[level] || console.log;
  if (payload === undefined) {
    fn(message);
    return;
  }
  fn(message, payload);
}

export function lobbyDebug(event, details = {}) {
  if (!isLobbyDebugEnabled()) {
    return;
  }
  const payload = {
    t: new Date().toISOString(),
    v: APP_VERSION,
    ...details
  };
  pushEvent(event, details);
  // warn = visible under Chrome "Default levels" (info is often hidden).
  emitLine('warn', `[dechess:lobby] ${event}`, payload);
}

/**
 * Pasteable multi-line report for chat/challenge delivery bugs.
 */
export function lobbyDebugReport(label, snapshot = {}) {
  if (!isLobbyDebugEnabled() || typeof console === 'undefined') {
    return null;
  }
  const report = {
    label: String(label || 'diag'),
    version: APP_VERSION,
    t: new Date().toISOString(),
    href: typeof location !== 'undefined' ? location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    snapshot,
    recentEvents: getLobbyDebugEvents().slice(-50)
  };
  lastReport = report;
  const text = [
    '',
    '===== DECHESS LOBBY DIAG (paste this block) =====',
    JSON.stringify(report, null, 2),
    '===== END DECHESS LOBBY DIAG =====',
    ''
  ].join('\n');
  emitLine('warn', text);
  emitLine(
    'warn',
    `[dechess:lobby] tip v${APP_VERSION}: run copyDeChessLobbyDiag() to copy; `
      + 'dumpDeChessLobbyDiag() to reprint. Filter console for "dechess".'
  );
  return report;
}

export function getLastLobbyDebugReport() {
  return lastReport;
}

export function formatLobbyDebugReport(report = lastReport) {
  if (!report) {
    return '';
  }
  return [
    '===== DECHESS LOBBY DIAG (paste this block) =====',
    JSON.stringify(report, null, 2),
    '===== END DECHESS LOBBY DIAG ====='
  ].join('\n');
}

export async function copyLobbyDebugReport(report = lastReport) {
  const text = formatLobbyDebugReport(report)
    || formatLobbyDebugReport(lobbyDebugReport('manual-copy', {
      note: 'No prior chat/challenge report; dumping recent events only.'
    }));
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    emitLine('warn', `[dechess:lobby] copied DIAG (${text.length} chars) to clipboard`);
    return { ok: true, via: 'clipboard', bytes: text.length };
  }
  emitLine('warn', text);
  return { ok: true, via: 'console', bytes: text.length };
}

/**
 * Periodic lobby health line — proves debug is alive and shows open vs visible peers.
 */
export function lobbyDebugHeartbeat(snapshot = {}) {
  if (!isLobbyDebugEnabled()) {
    return;
  }
  lobbyDebug('heartbeat', {
    ...snapshot,
    note: 'openPeerIds must be non-empty for WebRTC chat; Online now alone is not enough'
  });
}

/** Install window helpers + boot banner (once). */
export function installLobbyDebugBridge() {
  if (bridgeInstalled || typeof window === 'undefined' || isTestRuntime()) {
    return;
  }
  bridgeInstalled = true;

  // Force-enable if someone left debug off by accident during this outage.
  try {
    if (localStorage.getItem('dechessLobbyDebug') === '0') {
      localStorage.removeItem('dechessLobbyDebug');
      emitLine('warn', '[dechess:lobby] re-enabled debug (cleared dechessLobbyDebug=0)');
    }
  } catch {
    // Ignore.
  }

  window.dumpDeChessLobbyDiag = (label = 'manual-dump', snapshot = {}) => (
    lobbyDebugReport(label, snapshot)
  );
  window.copyDeChessLobbyDiag = () => copyLobbyDebugReport();
  window.__dechessLobbyDiag = {
    version: APP_VERSION,
    dump: window.dumpDeChessLobbyDiag,
    copy: window.copyDeChessLobbyDiag,
    events: getLobbyDebugEvents,
    last: getLastLobbyDebugReport,
    clear: clearLobbyDebugEvents,
    enabled: isLobbyDebugEnabled
  };

  if (!bootPrinted && isLobbyDebugEnabled()) {
    bootPrinted = true;
    emitLine(
      'warn',
      `[dechess:lobby] DEBUG ACTIVE v${APP_VERSION} — Default console levels OK (uses warn). `
        + 'Send chat/challenge → yellow [dechess:lobby] lines + DIAG block appear.'
    );
    lobbyDebugReport('BOOT', {
      role: 'boot',
      hint: 'If you never see CHAT_SEND after typing, the send path is not running on this tab.',
      signaling: 'dechess-org-peerjs.vercel.app',
      howToCopy: 'copyDeChessLobbyDiag()'
    });
  }
}
